import { FC, useMemo } from 'react';
import { useAtom } from 'jotai';
import { InlineNotification, Tag } from '@carbon/react';
import searchAtomPair, { type SearchParams } from 'atoms/search-atom';
import { ConfidenceBreakdown } from 'components/confidence-breakdown';
import { formatConfidence } from 'utils/number';
import {
  AuditLogTable,
  AgentRunDetail,
  EventExpanded,
} from 'pages/documents/structuring/audit-log';
import style from 'pages/documents/structuring/audit-log/audit-log.module.scss';
import type {
  ActivityFeedAgentRunRow,
  ActivityFeedItem,
  ActivityFeedRequest,
  ActivityFeedResponse,
  ActivityFeedSuggestion,
} from './types';

// Well Match Agent activity log. The AFE activity feed
// (POST /queries/afe_structuring_activity_feeds) serializes the same agent_run
// envelope as the Entity Match Agent feed, so this component reuses the shared
// audit-log renderers (pages/documents/structuring/audit-log) and supplies only
// the Well-specific suggestion row. It owns its own data fetch via
// `searchAtomPair`; the Parties log receives its rows as a prop instead.

type FeedRequestParams = SearchParams & ActivityFeedRequest;

interface Props {
  documentId: number | string;
  section: 'well' | 'cost' | 'identification';
  /**
   * Narrow the feed to a single well (its AfeTarget id). When set, the backend
   * scopes both the per-well events and the Well Matching Agent runs to this
   * target instead of every well on the document.
   */
  afeTargetId?: number | string;
}

const WellMatchSuggestionRow: FC<{ suggestion: ActivityFeedSuggestion }> = ({
  suggestion: s,
}) => (
  <div className={style.suggestionItem}>
    <div className={style.suggestionHeader}>
      <span className={style.suggestionName}>
        {s.suggestedWellName || `Well #${s.suggestedWellId ?? '—'}`}
      </span>
      <div className={style.suggestionMeta}>
        <span className={style.suggestionScore}>
          {formatConfidence(s.confidenceScore)}
        </span>
        <Tag type="gray" size="sm">
          {s.status}
        </Tag>
      </div>
    </div>
    {s.reviewReason && (
      <p className={style.suggestionReason}>Review reason: {s.reviewReason}</p>
    )}
    {s.confidenceFactors && s.confidenceFactors.length > 0 && (
      <ConfidenceBreakdown factors={s.confidenceFactors} />
    )}
  </div>
);

const SuggestionsList: FC<{ rows: ActivityFeedSuggestion[] }> = ({ rows }) => {
  if (rows.length === 0)
    return <p className={style.muted}>No suggestions persisted.</p>;
  return (
    <div className={style.suggestionsList}>
      {rows.map((s) => (
        <WellMatchSuggestionRow key={s.id} suggestion={s} />
      ))}
    </div>
  );
};

const renderAgentRun = (row: ActivityFeedAgentRunRow) => {
  const d = row.data;
  return (
    <AgentRunDetail
      meta={d}
      runId={row.id}
      suggestionsLabel="Suggestions Persisted"
      suggestionsCount={d.suggestions?.length ?? 0}
      suggestions={<SuggestionsList rows={d.suggestions ?? []} />}
    />
  );
};

const renderExpanded = (row: ActivityFeedItem) => {
  return row.activityType === 'agent_run' ? (
    renderAgentRun(row)
  ) : (
    <EventExpanded
      user={row.data.user}
      action={row.data.action}
      description={row.data.description}
    />
  );
};

export const AgentAuditLog: FC<Props> = ({
  documentId,
  section,
  afeTargetId,
}) => {
  const { searchAtom } = useMemo(
    () =>
      searchAtomPair<ActivityFeedItem, FeedRequestParams>(
        { href: '/queries/afe_structuring_activity_feeds' },
        {
          query: {
            document_id: documentId,
            section,
            // Scope to one well when given (omit the key entirely otherwise so
            // the BE's `afe_target_id.present?` check sees nothing to narrow by).
            ...(afeTargetId !== null && afeTargetId !== undefined
              ? { afe_target_id: afeTargetId }
              : {}),
            page: 1,
            page_size: 50,
            sort_direction: 'desc',
          },
        }
      ),
    [documentId, section, afeTargetId]
  );

  const [searchState] = useAtom(searchAtom);

  const activities =
    (searchState.data as unknown as ActivityFeedResponse | undefined)
      ?.results ?? [];

  if (searchState.error && activities.length === 0) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title="Unable to load activity"
        subtitle={searchState.error}
        style={{ maxWidth: '100%' }}
      />
    );
  }

  if (searchState.loading && activities.length === 0) {
    return <p className={style.empty}>Loading activity…</p>;
  }

  return (
    <AuditLogTable
      rows={activities}
      renderExpanded={renderExpanded}
      emptyMessage="No activity yet."
    />
  );
};
